<template>
  <base-drawer
    class="bulk-actions-drawer"
    :show="show"
    title="Bulk update"
    width="480px"
    @close="onClose"
  >
    <!-- Body -->
    <div :class="{ 'is-saving': saving }">
          <!-- Info banner -->
          <info-hint class="mb-3">
            {{ infoHintText }}
          </info-hint>

          <!-- Section: Action -->
          <drawer-section title="Action" icon="icon-settings-gear-63">
            <base-input>
              <el-select v-model="selectedAction" class="select-primary" value-key="id">
                <el-option
                  v-for="(ba, index) in actions"
                  class="select-primary"
                  :label="ba.label"
                  :value="{ id: ba.id, label: ba.label }"
                  :key="'BA' + index"
                />
              </el-select>
            </base-input>
          </drawer-section>

          <!-- Section: Status (only for action 1) -->
          <drawer-section v-if="selectedAction.id === 1" title="Status" icon="icon-tag">
            <base-input>
              <el-select v-model="selectedStatus" class="select-primary" value-key="id">
                <el-option
                  v-for="(status, index) in slotStatusOptions"
                  class="select-primary"
                  :label="status.label"
                  :value="{ id: status.id, label: status.label, status: status.status }"
                  :key="'STATUS' + index"
                />
              </el-select>
            </base-input>
          </drawer-section>

          <!-- Section: Booking time restrictions -->
          <drawer-section v-if="selectedAction.id === 2" title="Booking time restrictions" icon="icon-time-alarm">
            <value-period-input
              v-model="minBookingValue"
              :period.sync="minBookingPeriodId"
              :periods="minPeriods"
              :label="allowLateBooking ? 'Maximum time after booking start' : 'Minimum booking notice'"
            />
            <div class="toggle-row mt-3">
              <el-switch active-color="#e14eca" v-model="allowLateBooking" />
              <label class="mb-0 ml-2">Allow bookings after start time</label>
            </div>
            <div class="mt-3">
              <base-input
                :label="allowLateBooking ? 'Block off text (after start)' : 'Block off text (min notice)'"
                v-model="slotText"
              />
            </div>
            <div class="toggle-row mt-2">
              <el-switch active-color="#e14eca" v-model="hasExternalLink" />
              <label class="mb-0 ml-2">Include an external link</label>
            </div>
            <div v-if="hasExternalLink" class="mt-2">
              <base-input label="External link" v-model="externalLink" placeholder="https://example.com" />
            </div>
          </drawer-section>

          <!-- Section: Max bookings in advance -->
          <drawer-section v-if="selectedAction.id === 3" title="Maximum bookings in advance" icon="icon-calendar-60">
            <value-period-input
              v-model="maxBookingValue"
              :period.sync="maxBookingPeriodId"
              :periods="maxPeriods"
            />
          </drawer-section>

          <!-- Section: Clear resources -->
          <drawer-section v-if="selectedAction.id === 4" title="Clear resource assignments" icon="icon-single-02">
            <div class="toggle-row">
              <el-switch active-color="#e14eca" v-model="reassignToResourceSlots" />
              <label class="mb-0 ml-2">Attempt reassignment to resource slots</label>
            </div>
            <div class="toggle-row mt-2">
              <el-switch active-color="#e14eca" v-model="clearLockedAssignments" />
              <label class="mb-0 ml-2">Also clear locked assignments</label>
            </div>
          </drawer-section>

          <!-- Section: Booking note -->
          <drawer-section v-if="selectedAction.id === 5" title="Booking note" icon="icon-paper">
            <base-input :error="errs.bulk_note">
              <el-input
                v-model="note"
                :rows="4"
                type="textarea"
                placeholder="Add a new comment for slots in this range"
              />
            </base-input>
          </drawer-section>

          <!-- Section: Pricing -->
          <drawer-section v-if="selectedAction.id === 6 && enableCustomPricing" title="Pricing" icon="icon-money-coins">
            <div class="form-row">
              <div class="form-col">
                <base-input label="Pricing type">
                  <el-select v-model="customPricingType" class="select-primary" value-key="id" placeholder="Pricing type">
                    <el-option
                      v-for="(pt, index) in pricingTypes"
                      class="select-primary"
                      :label="pt.name"
                      :value="{ id: pt.id, name: pt.name }"
                      :key="'PT' + index"
                    />
                  </el-select>
                </base-input>
              </div>
              <div class="form-col">
                <base-input label="Reservation style">
                  <el-select v-model="isPublicBooking" class="select-primary" value-key="id" placeholder="Reservation style">
                    <el-option
                      v-for="(rs, index) in reservationStyles"
                      class="select-primary"
                      :label="rs.name"
                      :value="{ id: rs.id, name: rs.name }"
                      :key="'RS' + index"
                    />
                  </el-select>
                </base-input>
              </div>
            </div>

            <div class="toggle-row mt-2">
              <el-switch active-color="#e14eca" v-model="cumulativeCategoryPricing" />
              <label class="mb-0 ml-2">Category pricing is cumulative</label>
            </div>
            <div class="toggle-row mt-2">
              <el-switch active-color="#e14eca" v-model="hidePricingRate" />
              <label class="mb-0 ml-2">Hide pricing rate on booking site</label>
            </div>
            <div class="toggle-row mt-2">
              <el-switch active-color="#e14eca" v-model="limitPurchaseCategory" />
              <label class="mb-0 ml-2">Limit to one category per purchase
                <el-popover popper-class="otc-pop" trigger="hover" placement="top">
                  <div><div class="popover-body">When enabled, customers can only purchase one pricing category at a time. When disabled, customers can combine multiple categories in a single purchase.</div></div>
                  <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                </el-popover>
              </label>
            </div>

            <!-- Pricing categories -->
            <div class="mt-3">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="mb-0">Pricing categories</label>
                <base-button size="sm" type="primary" @click="addNewCategory">
                  <i class="tim-icons icon-simple-add mr-1"></i> Add category
                </base-button>
              </div>
              <div class="category-list">
                <div
                  v-for="(cat, catIdx) in customPricingCategories"
                  :key="'CAT' + catIdx"
                  class="category-card"
                >
                  <div class="category-card__header">
                    <base-input label="Name" class="mb-0 flex-grow-1">
                      <el-input size="small" v-model="cat.category" />
                    </base-input>
                    <base-button v-if="catIdx > 0" size="sm" link type="danger" class="mt-3" @click="removeCategory(catIdx)">
                      <i class="tim-icons icon-simple-remove"></i>
                    </base-button>
                  </div>
                  <div
                    v-for="(range, rangeIdx) in cat.pricing"
                    :key="'PR' + catIdx + '-' + rangeIdx"
                    class="price-range-row"
                  >
                    <base-input label="Min" style="width: 70px;">
                      <el-input-number size="small" v-model="range.min_players" :min="getMin(cat.pricing, rangeIdx)" :step="1" :controls="false" />
                    </base-input>
                    <base-input label="Max" style="width: 70px;">
                      <el-input-number size="small" v-model="range.max_players" :min="1" :step="1" :controls="false" />
                    </base-input>
                    <base-input :label="'Price (' + currencySymbol + ')'" style="width: 80px;">
                      <el-input-number size="small" v-model="range.price" :controls="false" :precision="2" :step="0.1" />
                    </base-input>
                    <base-button v-if="rangeIdx > 0" size="sm" link type="danger" class="mt-3" @click="removePriceRange(cat.pricing, rangeIdx)">
                      <i class="tim-icons icon-simple-remove"></i>
                    </base-button>
                    <base-button v-if="rangeIdx === cat.pricing.length - 1" size="sm" link type="success" class="mt-3" @click="addPriceRange(cat)">
                      <i class="tim-icons icon-simple-add"></i>
                    </base-button>
                  </div>
                  <div class="participant-requirements">
                    <div class="form-row">
                      <div class="form-col">
                        <base-input label="Min required" style="max-width: 120px;">
                          <el-input-number size="small" :controls="false" v-model.number="cat.minimum_participants" :min="0" :step="1" />
                        </base-input>
                      </div>
                      <div class="form-col">
                        <base-input label="Max allowed" style="max-width: 120px;">
                          <el-input-number size="small" :controls="false" v-model.number="cat.maximum_participants" :step="1" />
                        </base-input>
                      </div>
                      <div class="form-col">
                        <base-input label="Default" style="max-width: 120px;">
                          <el-input-number size="small" :controls="false" v-model.number="cat.default_participants" :step="1" />
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Deposits -->
            <div class="mt-3" style="border-top: 1px solid rgba(255,255,255,0.06); padding-top: 12px;">
              <div class="toggle-row">
                <el-switch active-color="#e14eca" v-model="allowDeposit" />
                <label class="mb-0 ml-2">Allow deposit</label>
              </div>
              <div v-if="allowDeposit" class="toggle-row mt-2">
                <el-switch active-color="#e14eca" v-model="requireDeposit" />
                <label class="mb-0 ml-2">Require only deposit amount during checkout</label>
              </div>
              <div v-if="allowDeposit" class="form-row mt-2">
                <div class="form-col">
                  <base-input label="Deposit type">
                    <el-select v-model="depositType" class="select-primary" value-key="id">
                      <el-option
                        v-for="(type, index) in valueTypes"
                        class="select-primary"
                        :label="type.name"
                        :value="{ name: type.name, id: type.id }"
                        :key="'VT' + index"
                      />
                    </el-select>
                  </base-input>
                </div>
                <div class="form-col">
                  <base-input :label="'Deposit amount (' + currencySymbol + ')'" v-model="depositAmount" placeholder="Deposit amount" />
                </div>
              </div>
            </div>
          </drawer-section>

          <!-- Section: Custom message -->
          <drawer-section v-if="selectedAction.id === 7" title="Custom message" icon="icon-chat-33">
            <info-hint class="mb-2">
              This text shows in addition to any other text on the customer-facing slot on the booking site.
            </info-hint>
            <div class="toggle-row mb-3">
              <el-switch active-color="#e14eca" v-model="customMessageActive" />
              <label class="mb-0 ml-2">Show message on this booking slot</label>
            </div>
            <template v-if="customMessageActive">
              <base-input label="Message" :error="errs.custom_message">
                <el-input
                  v-model="customMessage"
                  :maxlength="200"
                  placeholder="e.g. Closed for Christmas!"
                />
              </base-input>
              <small v-if="customMessageDisplay === 'badge' && customMessage.length > 20" style="color: rgba(255, 255, 255, 0.7);">
                Badges read best with 20 characters or fewer.
              </small>
              <label class="text-white d-block mt-2">Display style</label>
              <segmented-toggle
                v-model="customMessageDisplay"
                :options="customMessageDisplayOptions"
                size="sm"
              />
            </template>
            <base-input label="Apply to slots with status" class="mt-3">
              <el-select v-model="customMessageStatusFilter" class="select-primary" placeholder="All statuses">
                <el-option
                  v-for="opt in customMessageStatusOptions"
                  class="select-primary"
                  :key="'CMS-' + (opt.value || 'all')"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
            </base-input>
          </drawer-section>

          <!-- Section: Assign staff -->
          <drawer-section v-if="selectedAction.id === 8" title="Assign staff" icon="icon-single-02">
            <base-input label="Add staff (search by name or email)">
              <el-autocomplete
                style="width: 100%"
                v-model="staffSearchValue"
                :fetch-suggestions="querySearchStaff"
                placeholder="Search"
                :trigger-on-focus="false"
                @select="handleSelectStaff"
              >
                <template #default="{ item }">
                  <p style="margin-bottom: -12px" class="text-primary">{{ item.first_name }} {{ item.last_name }}</p>
                  <small class="font-italic">
                    <i class="tim-icons icon-email-85"></i> {{ item.email }} &nbsp;
                  </small>
                </template>
              </el-autocomplete>
            </base-input>
            <div v-if="staffToAssign.length > 0" class="staff-chip-list">
              <span
                v-for="(member, index) in staffToAssign"
                :key="'STAFF' + member.id"
                class="staff-chip"
              >
                {{ member.first_name }} {{ member.last_name }}
                <i class="tim-icons icon-simple-remove staff-chip__remove" @click="removeStaffMember(index)"></i>
              </span>
            </div>
          </drawer-section>

          <!-- Section: Schedule template (action 9) -->
          <drawer-section v-if="selectedAction.id === 9" title="Schedule template" icon="icon-calendar-60">
            <base-input label="Use slot times from">
              <el-select
                v-model="selectedTemplate"
                class="select-primary"
                value-key="id"
                placeholder="Select a schedule"
                :loading="schedulesLoading"
              >
                <el-option-group label="Booking schedules">
                  <el-option
                    v-for="s in bookingSchedules"
                    class="select-primary"
                    :key="'BS' + s.id"
                    :label="s.name"
                    :value="{ id: s.id, name: s.name, type: 0 }"
                  />
                </el-option-group>
                <el-option-group label="Resource schedules">
                  <el-option
                    v-for="s in resourceSchedules"
                    class="select-primary"
                    :key="'RS' + s.id"
                    :label="s.name"
                    :value="{ id: s.id, name: s.name, type: 1 }"
                  />
                </el-option-group>
              </el-select>
            </base-input>
          </drawer-section>

          <!-- Section: Events -->
          <drawer-section v-if="selectedAction.id !== 4 && !(selectedAction.id === 9 && selectedTemplate && selectedTemplate.type === 1)" title="Events" icon="icon-puzzle-10">
            <base-input>
              <el-select multiple clearable v-model="selectedGames" class="select-primary" value-key="id" placeholder="Select events">
                <el-option
                  v-for="(ga, index) in games"
                  class="select-primary"
                  :label="ga.name"
                  :value="{ id: ga.id, name: ga.name }"
                  :key="'GA' + index"
                />
              </el-select>
            </base-input>
          </drawer-section>

          <!-- Section: Date range -->
          <drawer-section title="Date range" icon="icon-calendar-60">
            <div class="form-row">
              <div class="form-col">
                <base-input label="From" :error="errs.bulk_from">
                  <el-date-picker
                    :editable="false"
                    type="date"
                    placeholder="Select date"
                    :format="dayOfWeek + dateFormatCal"
                    v-model="bulkFrom"
                    :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
                  />
                </base-input>
              </div>
              <div class="form-col">
                <base-input label="To" :error="errs.bulk_to">
                  <el-date-picker
                    :editable="false"
                    type="date"
                    placeholder="Select date"
                    :format="dayOfWeek + dateFormatCal"
                    v-model="bulkTo"
                    :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
                  />
                </base-input>
              </div>
            </div>
            <div v-if="selectedAction.id !== 9" class="toggle-row mt-2">
              <el-switch active-color="#e14eca" v-model="timeRangeOn" />
              <label class="mb-0 ml-2">Limit to a specific time range</label>
            </div>
            <div class="form-row mt-2" v-if="timeRangeOn">
              <div class="form-col">
                <base-input label="Start time" :error="errs.bulk_from_time">
                  <el-time-picker v-model="bulkFromTime" :format="pickerTimeFormat" placeholder="HH:MM" />
                </base-input>
              </div>
              <div class="form-col">
                <base-input label="End time" :error="errs.bulk_to_time">
                  <el-time-picker v-model="bulkToTime" :format="pickerTimeFormat" placeholder="HH:MM" />
                </base-input>
              </div>
            </div>
          </drawer-section>

          <!-- Section: Conflict review (action 9, after the existing-slot check) -->
          <drawer-section
            v-if="selectedAction.id === 9 && conflictCheckDone && hasConflicts"
            title="Existing slots in this range"
            icon="icon-alert-circle-exc"
          >
            <info-hint class="mb-3">
              Existing slots stay unchanged unless selected below. Choose what to do where the template overlaps them.
            </info-hint>
            <div v-for="row in conflictRows" v-if="row.count > 0" :key="'CR' + row.status" class="category-card mb-3">
              <label class="d-block">{{ row.label }} ({{ row.count }})</label>
              <template v-if="!row.removable">
                <div class="toggle-row">
                  <el-switch active-color="#e14eca" v-model="row.create_overlap" :disabled="row.count <= 0" />
                  <label class="mb-0 ml-2">Create overlapping slots</label>
                </div>
              </template>
              <template v-else>
                <div class="toggle-row">
                  <el-switch active-color="#e14eca" v-model="row.remove" :disabled="row.count <= 0" />
                  <label class="mb-0 ml-2">Remove existing slots</label>
                </div>
                <div class="toggle-row mt-1">
                  <el-switch active-color="#e14eca" v-model="row.create_overlap" :disabled="row.count <= 0" />
                  <label class="mb-0 ml-2">Create overlapping slots</label>
                </div>
              </template>
              <div v-if="row.create_overlap" class="mt-2">
                <segmented-toggle v-model="row.skip_mode" :options="skipModeOptions" size="sm" />
              </div>
            </div>
          </drawer-section>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="onSubmit">
          {{ submitLabel }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script>
import { API_LOCATION } from '@/api-config';
import { buildFutureSlotRows, buildFutureSlotFlags, SKIP_MODE_OPTIONS } from '@/util/scheduleFutureSlots';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import moment from 'moment';
import swal from 'sweetalert2';
import {
  Select,
  Option,
  OptionGroup,
  Switch,
  Input,
  InputNumber,
  DatePicker,
  TimePicker,
  Popover,
  Autocomplete
} from 'element-ui';

export default {
  name: 'BulkActionsDrawer',

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [OptionGroup.name]: OptionGroup,
    [Switch.name]: Switch,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber,
    [DatePicker.name]: DatePicker,
    [TimePicker.name]: TimePicker,
    [Popover.name]: Popover,
    [Autocomplete.name]: Autocomplete
  },

  props: {
    show: { type: Boolean, default: false },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    /** Games available for selection */
    games: { type: Array, default: () => [] },
    /** Initial date for the from/to range */
    initialDate: { type: [String, Date], default: null },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    dateFormatCal: { type: String, default: 'MM-dd-yyyy' },
    dayOfWeek: { type: String, default: '' },
    timeFormat: { type: String, default: '12h' },
    firstDayOfWeek: { type: Number, default: 0 },
    currencySymbol: { type: String, default: '$' },
    /** Pricing types for action 6. Mirrors game_pricing_types (ids 1 and 2); parents may override with the live list. */
    pricingTypes: {
      type: Array,
      default: () => [
        { id: 1, name: 'Per person' },
        { id: 2, name: 'Flat' }
      ]
    }
  },

  data() {
    return {
      saving: false,

      // Action selection
      selectedAction: { id: 1, label: 'Change slot status' },
      actions: [
        { id: 1, label: 'Change slot status' },
        { id: 2, label: 'Change minimum booking notice' },
        { id: 3, label: 'Change maximum booking in advance' },
        { id: 4, label: 'Clear resource assignments' },
        { id: 5, label: 'Add a booking note' },
        { id: 6, label: 'Change slot pricing' },
        { id: 7, label: 'Set custom message' },
        { id: 8, label: 'Assign staff' },
        { id: 9, label: 'Create slots from a schedule' }
      ],

      // Action 9: create slots from a schedule template
      schedulesLoading: false,
      bookingSchedules: [],
      resourceSchedules: [],
      selectedTemplate: null,
      conflictCheckDone: false,
      conflictRows: [],
      skipModeOptions: SKIP_MODE_OPTIONS,

      // Action 8: Assign staff
      staffSearchValue: '',
      staffToAssign: [],

      // Action 7: Custom message
      customMessage: '',
      customMessageDisplay: 'text',
      customMessageActive: true,
      customMessageStatusFilter: null,
      customMessageDisplayOptions: [
        { value: 'text', label: 'Text' },
        { value: 'badge', label: 'Badge' }
      ],
      customMessageStatusOptions: [
        { value: null, label: 'All statuses' },
        { value: 'available', label: 'Available' },
        { value: 'blocked', label: 'Blocked' },
        { value: 'call_to_book', label: 'Call to book' },
        { value: 'booked', label: 'Booked' }
      ],

      // Action 1: Status
      selectedStatus: { id: 1, label: 'Available', status: 'available' },
      slotStatuses: [
        { id: 1, label: 'Available', status: 'available' },
        { id: 2, label: 'Blocked', status: 'blocked' },
        { id: 3, label: 'Call to book', status: 'call_to_book' },
        { id: 4, label: 'Requestable', status: 'requestable' }
      ],
      // Active custom statuses for this group, loaded when the drawer opens
      customStatuses: [],

      // Action 2: Min booking notice
      minBookingValue: 1,
      minBookingPeriodId: 1,
      minPeriods: [
        { id: 0, label: 'minute(s)' },
        { id: 1, label: 'hour(s)' },
        { id: 2, label: 'day(s)' },
        { id: 3, label: 'week(s)' },
        { id: 4, label: 'month(s)' }
      ],
      allowLateBooking: false,
      slotText: '',
      hasExternalLink: false,
      externalLink: '',

      // Action 3: Max booking advance
      maxBookingValue: 6,
      maxBookingPeriodId: 2,
      maxPeriods: [
        { id: 0, label: 'day(s)' },
        { id: 1, label: 'week(s)' },
        { id: 2, label: 'month(s)' },
        { id: 3, label: 'year(s)' }
      ],

      // Action 4: Clear resources
      reassignToResourceSlots: false,
      clearLockedAssignments: false,

      // Action 5: Notes
      note: '',

      // Action 6: Pricing
      enableCustomPricing: true,
      customPricingType: { id: 1 },
      reservationStyles: [
        { id: 0, name: 'Private' },
        { id: 1, name: 'Public' }
      ],
      isPublicBooking: { id: 0 },
      cumulativeCategoryPricing: 1,
      hidePricingRate: 0,
      limitPurchaseCategory: 0,
      customPricingCategories: [],
      allowDeposit: 0,
      requireDeposit: 0,
      depositType: { id: 0 },
      depositAmount: 1,
      valueTypes: [
        { id: 0, name: 'Per booking' },
        { id: 1, name: 'Per participant' }
      ],

      // Game/date selection
      selectedGames: [],
      bulkFrom: new Date(),
      bulkTo: new Date(),
      timeRangeOn: false,
      bulkFromTime: new Date(),
      bulkToTime: new Date(),

      // Errors
      errs: {
        bulk_from: '',
        bulk_to: '',
        bulk_from_time: '',
        bulk_to_time: '',
        bulk_note: ''
      }
    };
  },

  computed: {
    pickerTimeFormat() {
      return (this.timeFormat === '24h' || this.timeFormat === 'HH:mm') ? 'HH:mm' : 'hh:mm A';
    },

    // Built-in statuses plus the group's active custom statuses
    slotStatusOptions() {
      const custom = this.customStatuses
        .filter(s => s.is_active)
        .map(s => ({ id: 'c' + s.id, label: s.display_name, status: s.status_key }));
      return [...this.slotStatuses, ...custom];
    },

    hasConflicts() {
      return this.conflictRows.some(r => r.count > 0);
    },

    submitLabel() {
      if (this.saving) return this.selectedAction.id === 9 ? 'Working...' : 'Updating...';
      return this.selectedAction.id === 9 ? 'Create slots' : 'Update slots';
    },

    infoHintText() {
      if (this.selectedAction.id === 9) {
        return 'Creates new slots using the selected schedule\'s weekly slot times as a template. The new slots are manual slots: they are not linked to the schedule and are limited to 14 days at a time.';
      }
      if (this.selectedAction.id === 1) {
        return 'Bulk status changes update every slot in the selected time frame, including slots that already have a custom status. Booked, completed, cancelled, checked, and expired slots are never changed.';
      }
      if (this.selectedAction.id === 8) {
        return 'The selected staff are added to every slot for the chosen events in this time frame, regardless of status. Staff already assigned to a slot are kept, never removed.';
      }
      return 'Bulk actions will update all slots marked as available, blocked, or call to book with the specified action in the selected time frame. All other slots will remain unchanged.';
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.resetForm();
        this.loadCustomStatuses();
        this.loadSchedules();
        document.addEventListener('keydown', this.handleEscape);
      } else {
        document.removeEventListener('keydown', this.handleEscape);
      }
    },

    selectedAction(val) {
      if (val && val.id === 9) this.timeRangeOn = false;
      this.resetConflictCheck();
    },
    selectedTemplate() {
      this.resetConflictCheck();
    },
    bulkFrom() {
      this.resetConflictCheck();
    },
    bulkTo() {
      this.resetConflictCheck();
    },
    selectedGames() {
      this.resetConflictCheck();
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape);
  },

  methods: {
    handleEscape(e) {
      if (e.key === 'Escape') this.onClose();
    },

    resetForm() {
      this.reassignToResourceSlots = false;
      this.clearLockedAssignments = false;
      let parsedDate;
      if (this.initialDate instanceof Date) {
        parsedDate = this.initialDate;
      } else if (typeof this.initialDate === 'string' && this.initialDate.length > 0) {
        // Strip to 10 chars and parse as local time (avoids UTC midnight TZ shift)
        parsedDate = moment(this.initialDate.substring(0, 10), 'YYYY-MM-DD').toDate();
      } else {
        parsedDate = new Date();
      }
      this.bulkFrom = parsedDate;
      this.bulkTo = parsedDate;
      this.selectedGames = this.games.map(g => ({ id: g.id, name: g.name }));
      this.selectedAction = { id: 1, label: 'Change slot status' };
      this.selectedStatus = { id: 1, label: 'Available', status: 'available' };
      this.minBookingPeriod = { id: 1, label: 'hour(s)' };
      this.minBookingValue = 1;
      this.allowLateBooking = false;
      this.maxBookingPeriod = { id: 2, label: 'month(s)' };
      this.maxBookingValue = 6;
      this.slotText = '';
      this.hasExternalLink = false;
      this.externalLink = '';
      this.note = '';
      this.enableCustomPricing = true;
      this.customPricingType = { id: 1 };
      this.cumulativeCategoryPricing = 1;
      this.hidePricingRate = 0;
      this.limitPurchaseCategory = 0;
      this.allowDeposit = 0;
      this.requireDeposit = 0;
      this.depositType = { id: 0 };
      this.depositAmount = 1;
      this.isPublicBooking = { id: 0 };
      this.customPricingCategories = [];
      this.addNewCategory();
      this.timeRangeOn = false;
      this.customMessage = '';
      this.customMessageDisplay = 'text';
      this.customMessageActive = true;
      this.customMessageStatusFilter = null;
      this.staffSearchValue = '';
      this.staffToAssign = [];
      this.selectedTemplate = null;
      this.resetConflictCheck();
      this.errs = { bulk_from: '', bulk_to: '', bulk_from_time: '', bulk_to_time: '', bulk_note: '', custom_message: '' };
    },

    resetConflictCheck() {
      this.conflictCheckDone = false;
      this.conflictRows = [];
    },

    async loadSchedules() {
      this.schedulesLoading = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/schedules`;
        const response = await axios.get(url, config);
        this.bookingSchedules = response.data.schedules || [];
        this.resourceSchedules = response.data.resource_schedules || [];
      } catch (err) {
        console.log(err);
        this.bookingSchedules = [];
        this.resourceSchedules = [];
      } finally {
        this.schedulesLoading = false;
      }
    },

    validateForm() {
      let errors = 0;

      if (!this.bulkTo) {
        this.errs.bulk_to = 'Invalid end date';
        errors++;
      } else {
        this.errs.bulk_to = '';
      }

      if (this.bulkFrom && this.bulkTo && this.bulkFrom > this.bulkTo) {
        this.errs.bulk_from = 'Start date cannot be greater than end date';
        errors++;
      } else if (!this.bulkFrom) {
        this.errs.bulk_from = 'Invalid start date';
        errors++;
      } else {
        this.errs.bulk_from = '';
      }

      const MAX_RANGE_DAYS = this.selectedAction.id === 9 ? 14 : 30;
      if (this.bulkFrom && this.bulkTo && this.bulkFrom <= this.bulkTo) {
        const daysDiff = moment(this.bulkTo).diff(moment(this.bulkFrom), 'days') + 1;
        if (daysDiff > MAX_RANGE_DAYS) {
          this.errs.bulk_to = `Max ${MAX_RANGE_DAYS} days`;
          this.$notify({
            type: 'warning',
            message: `Bulk actions are limited to ${MAX_RANGE_DAYS} days at a time. Your selected range is ${daysDiff} days. Please narrow it down.`,
            timeout: 5000
          });
          errors++;
        }
      }

      if (this.timeRangeOn && !this.bulkToTime) {
        this.errs.bulk_to_time = 'Invalid end time';
        errors++;
      } else {
        this.errs.bulk_to_time = '';
      }

      // Compare time of day only: typed picker input parses to Jan 1 while
      // panel-picked values keep today's date, so full Dates aren't comparable
      if (this.timeRangeOn && this.bulkFromTime && this.bulkToTime
        && moment(this.bulkFromTime).format('HH:mm') > moment(this.bulkToTime).format('HH:mm')) {
        this.errs.bulk_from_time = 'Start time cannot be greater than end time';
        errors++;
      } else if (this.timeRangeOn && !this.bulkFromTime) {
        this.errs.bulk_from_time = 'Invalid start time';
        errors++;
      } else {
        this.errs.bulk_from_time = '';
      }

      if (this.selectedAction.id === 5 && this.note === '') {
        this.errs.bulk_note = 'Include a note';
        errors++;
      } else {
        this.errs.bulk_note = '';
      }

      if (this.selectedAction.id === 7 && this.customMessageActive && (!this.customMessage || !this.customMessage.trim())) {
        this.errs.custom_message = 'Add a message before enabling, or turn the toggle off to clear messages';
        this.$notify({ type: 'warning', message: 'Add a message before enabling', timeout: 3000 });
        errors++;
      } else {
        this.errs.custom_message = '';
      }

      if (this.selectedAction.id === 9 && !this.selectedTemplate) {
        this.$notify({ type: 'warning', message: 'Select a schedule template', timeout: 3000 });
        errors++;
      }

      const isResourceTemplate = this.selectedAction.id === 9
        && this.selectedTemplate && this.selectedTemplate.type === 1;
      if (this.selectedAction.id !== 4 && !isResourceTemplate && this.selectedGames.length === 0) {
        this.$notify({ type: 'warning', message: 'Select at least one event', timeout: 3000 });
        errors++;
      }

      if (this.selectedAction.id === 8 && this.staffToAssign.length === 0) {
        this.$notify({ type: 'warning', message: 'Add at least one staff member', timeout: 3000 });
        errors++;
      }

      return errors === 0;
    },

    async onSubmit() {
      if (!this.validateForm()) return;

      const dateFrom = moment(this.bulkFrom).format(this.dateFormat);
      const dateTo = moment(this.bulkTo).format(this.dateFormat);

      // Action 9 checks for existing slots first; when there are conflicts the
      // review section appears and the user confirms by pressing the button again
      if (this.selectedAction.id === 9 && this.selectedTemplate.type === 0 && !this.conflictCheckDone) {
        const checked = await this.checkExistingSlots();
        if (!checked) return;
        if (this.hasConflicts) {
          this.$notify({
            type: 'info',
            message: 'There are existing slots in this range. Review the options below, then press create slots again.',
            timeout: 6000
          });
          return;
        }
      }

      let actionPhrase
      switch (this.selectedAction.id) {
        case 1:
          actionPhrase = `set status to "${this.selectedStatus.label}"`;
          break;
        case 2:
          actionPhrase = 'update minimum booking notice';
          break;
        case 3:
          actionPhrase = 'update maximum bookings in advance';
          break;
        case 4:
          actionPhrase = 'clear resource assignments';
          break;
        case 5:
          actionPhrase = 'add a booking note';
          break;
        case 6:
          actionPhrase = 'update slot pricing';
          break;
        case 7:
          actionPhrase = 'set a custom message';
          break;
        case 8:
          actionPhrase = `assign ${this.staffToAssign.length} staff member${this.staffToAssign.length === 1 ? '' : 's'}`;
          break;
        case 9:
          actionPhrase = `create slots from "${this.selectedTemplate.name}"`;
          break;
        default:
          actionPhrase = 'update slots';
      }

      const isCreate = this.selectedAction.id === 9;
      const result = await swal({
        title: isCreate ? 'Confirm slot creation' : 'Confirm bulk update',
        text: isCreate
          ? `This will ${actionPhrase} between ${dateFrom} and ${dateTo}.`
          : `This will ${actionPhrase} for slots between ${dateFrom} and ${dateTo}.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: isCreate ? 'Create slots' : 'Update slots',
        cancelButtonText: 'Go back',
        confirmButtonClass: 'btn btn-primary',
        cancelButtonClass: 'btn btn-secondary',
        buttonsStyling: false
      });

      if (!result.value) return;

      if (this.selectedAction.id === 4) {
        await this.bulkUpdateResources();
      } else if (this.selectedAction.id === 8) {
        await this.bulkAssignStaff();
      } else if (this.selectedAction.id === 9) {
        await this.bulkCreateSlots();
      } else {
        await this.bulkUpdate();
      }
    },

    async checkExistingSlots() {
      this.saving = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/schedules/check-future-slots`;

        const data = {
          today: moment().format('YYYY-MM-DD'),
          start_date: moment(this.bulkFrom).format('YYYY-MM-DD'),
          end_date: moment(this.bulkTo).format('YYYY-MM-DD'),
          games: this.selectedGames.map(g => g.id),
          resources: []
        };

        const response = await axios.post(url, data, config);
        const body = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        this.conflictRows = buildFutureSlotRows(body);
        this.conflictCheckDone = true;
        return true;
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          let message = 'Something went wrong';
          try {
            const body = typeof err.response?.data === 'string'
              ? JSON.parse(err.response.data)
              : err.response?.data;
            if (body?.error) message = body.error;
            else if (body?.message) message = body.message;
          } catch (_) {}
          this.$notify({ type: 'danger', message, timeout: 5000 });
        }
        return false;
      } finally {
        this.saving = false;
      }
    },

    async bulkCreateSlots() {
      this.saving = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}`
          + `/schedules/${this.selectedTemplate.id}/bulk-create-slots`;

        const data = {
          start_date: moment(this.bulkFrom).format('YYYY-MM-DD'),
          end_date: moment(this.bulkTo).format('YYYY-MM-DD'),
          time: moment().format('YYYY-MM-DD'),
          events: this.selectedTemplate.type === 0 ? this.selectedGames.map(g => ({ id: g.id })) : [],
          ...buildFutureSlotFlags(this.conflictRows)
        };

        await axios.post(url, data, config);
        this.$notify({
          type: 'success',
          message: 'Slot creation started. New slots will appear on the calendar shortly.',
          timeout: 5000
        });
        this.$emit('updated');
        this.onClose();
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          let message = 'Something went wrong';
          try {
            const body = typeof err.response?.data === 'string'
              ? JSON.parse(err.response.data)
              : err.response?.data;
            if (body?.error) message = body.error;
            else if (body?.message) message = body.message;
          } catch (_) {}
          this.$notify({ type: 'danger', message, timeout: 5000 });
        }
      } finally {
        this.saving = false;
      }
    },

    async loadCustomStatuses() {
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/booking-slot-settings`;
        const response = await axios.get(url, config);
        this.customStatuses = (response.data.custom_statuses || []).filter(s => s.is_active);
      } catch (err) {
        console.log(err);
        this.customStatuses = [];
      }
    },

    async bulkUpdate() {
      this.saving = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings`;

        const data = {
          action: this.selectedAction.id,
          min_booking_value: this.minBookingValue,
          min_booking_period: this.minBookingPeriodId,
          allow_late_booking: this.allowLateBooking,
          slot_text: this.slotText,
          external_link: this.externalLink,
          has_external_link: this.hasExternalLink,
          max_booking_period: this.maxBookingPeriodId,
          max_booking_value: this.maxBookingValue,
          status: this.selectedStatus.status,
          to: moment(this.bulkTo).format('YYYY-MM-DD'),
          from: moment(this.bulkFrom).format('YYYY-MM-DD'),
          time_to: moment(this.bulkToTime).format('HH:mm'),
          time_from: moment(this.bulkFromTime).format('HH:mm'),
          has_time_range: this.timeRangeOn,
          events: this.selectedGames,
          note: this.note,
          pricing_type_id: this.customPricingType.id,
          allow_deposit: this.allowDeposit,
          cumulative_category_pricing: this.cumulativeCategoryPricing,
          hide_pricing_rate: this.hidePricingRate,
          limit_purchase_category: this.limitPurchaseCategory,
          deposit_amount: this.depositAmount,
          require_deposit: this.requireDeposit,
          deposit_type: this.depositType.id,
          custom_pricing: this.customPricingCategories,
          is_public_booking: this.isPublicBooking.id,
          custom_message: this.customMessage,
          custom_message_display: this.customMessageDisplay,
          custom_message_active: this.customMessageActive ? 1 : 0,
          status_filter: this.customMessageStatusFilter || null
        };

        await axios.put(url, data, config);
        this.$notify({ type: 'success', message: 'Slots updated', timeout: 3000 });
        this.$emit('updated');
        this.onClose();
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          let message = 'Something went wrong';
          try {
            const body = typeof err.response?.data === 'string'
              ? JSON.parse(err.response.data)
              : err.response?.data;
            if (body?.message) message = body.message;
          } catch (_) {}
          this.$notify({ type: 'danger', message, timeout: 5000 });
        }
      } finally {
        this.saving = false;
      }
    },

    async bulkUpdateResources() {
      this.saving = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };

        const start = moment(this.bulkFrom).format('YYYY-MM-DD');
        const end = moment(this.bulkTo).format('YYYY-MM-DD');
        const startTime = moment(this.bulkFromTime).format('HH:mm');
        const endTime = moment(this.bulkToTime).format('HH:mm');

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}`
          + `/staff/clear?start=${start}&end=${end}&reassign=${Number(this.reassignToResourceSlots)}`
          + `&clear_locked=${Number(this.clearLockedAssignments)}`
          + `&has_time_range=${Number(this.timeRangeOn)}`
          + `&time_from=${startTime}&time_to=${endTime}`;

        await axios.delete(url, config);
        this.$notify({ type: 'success', message: 'Resource assignments cleared', timeout: 3000 });
        this.$emit('updated');
        this.onClose();
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          let message = 'Something went wrong';
          try {
            const body = typeof err.response?.data === 'string'
              ? JSON.parse(err.response.data)
              : err.response?.data;
            if (body?.message) message = body.message;
          } catch (_) {}
          this.$notify({ type: 'danger', message, timeout: 5000 });
        }
      } finally {
        this.saving = false;
      }
    },

    // Action 8: staff assignment
    async querySearchStaff(queryString, cb) {
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}`
          + `/company-users?count=10000000&autocomplete=${encodeURIComponent(queryString)}`;
        const response = await axios.get(url, config);
        const results = (response.data.company_users || []).map(u => ({
          value: `${u.first_name} ${u.last_name}`,
          id: u.id,
          first_name: u.first_name,
          last_name: u.last_name,
          email: u.email
        }));
        cb(results);
      } catch (err) {
        console.log(err);
        cb([]);
      }
    },

    handleSelectStaff(item) {
      if (this.staffToAssign.some(s => s.id === item.id)) {
        this.staffSearchValue = '';
        return;
      }
      this.staffToAssign.push({
        id: item.id,
        first_name: item.first_name,
        last_name: item.last_name,
        email: item.email
      });
      this.staffSearchValue = '';
    },

    removeStaffMember(index) {
      this.staffToAssign.splice(index, 1);
    },

    async bulkAssignStaff() {
      this.saving = true;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/staff/bulk-assign`;

        const data = {
          staff: this.staffToAssign,
          events: this.selectedGames,
          from: moment(this.bulkFrom).format('YYYY-MM-DD'),
          to: moment(this.bulkTo).format('YYYY-MM-DD'),
          has_time_range: this.timeRangeOn,
          time_from: moment(this.bulkFromTime).format('HH:mm'),
          time_to: moment(this.bulkToTime).format('HH:mm')
        };

        const response = await axios.post(url, data, config);
        let assigned = null;
        try {
          const body = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
          assigned = body && typeof body.assigned_slots === 'number' ? body.assigned_slots : null;
        } catch (_) {}
        const message = assigned !== null
          ? `Staff assigned to ${assigned} booking${assigned === 1 ? '' : 's'}`
          : 'Staff assigned';
        this.$notify({ type: 'success', message, timeout: 3000 });
        this.$emit('updated');
        this.onClose();
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          let message = 'Something went wrong';
          try {
            const body = typeof err.response?.data === 'string'
              ? JSON.parse(err.response.data)
              : err.response?.data;
            if (body?.error) message = body.error;
            else if (body?.message) message = body.message;
          } catch (_) {}
          this.$notify({ type: 'danger', message, timeout: 5000 });
        }
      } finally {
        this.saving = false;
      }
    },

    // Pricing helpers (identical to Bookings.vue)
    addNewCategory() {
      this.customPricingCategories.push({
        category: 'Person',
        minimum_participants: 0,
        maximum_participants: null,
        default_participants: 0,
        pricing: [{ min_players: 1, max_players: 2, price: 0 }]
      });
    },

    removeCategory(index) {
      this.customPricingCategories.splice(index, 1);
    },

    addPriceRange(category) {
      if (category.pricing.length > 0) {
        const last = category.pricing[category.pricing.length - 1];
        category.pricing.push({
          min_players: last.max_players + 1,
          max_players: last.max_players + 2,
          price: 0
        });
      } else {
        category.pricing.push({ min_players: 1, max_players: 2, price: 0 });
      }
    },

    removePriceRange(pricingArray, index) {
      pricingArray.splice(index, 1);
    },

    getMin(pricing, index) {
      if (index < 1) return 1;
      return pricing[index - 1].max_players + 1;
    },

    onClose() {
      this.$emit('update:show', false);
      this.$emit('close');
    }
  }
};
</script>

<style lang="scss" scoped>
.bulk-actions-drawer {
  ::v-deep label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8571em;
    font-weight: 400;
    text-transform: none;
    letter-spacing: normal;
    line-height: 1.42857;
    margin-bottom: 5px;
  }
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.is-saving {
  opacity: 0.6;
  pointer-events: none;
}

// ============================================
// Form elements
// ============================================
.form-row {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.form-col {
  flex: 1;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    margin-bottom: 0;
  }
}

// ============================================
// Assign staff chips
// ============================================
.staff-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.staff-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 11px;
  background: rgba(225, 78, 202, 0.15);
  border: 1px solid #e14eca;
  color: #e14eca;
  white-space: nowrap;

  &__remove {
    cursor: pointer;
    font-size: 10px;
  }
}

// ============================================
// Pricing categories
// ============================================
.category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px;

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
}

.price-range-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
}

.participant-requirements {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

// ============================================
// Light mode
// ============================================
.white-content {
  .bulk-actions-drawer ::v-deep label {
    color: rgba(52, 70, 117, 0.7);
  }

  .category-card {
    background: rgba(52, 70, 117, 0.03);
    border-color: #e3e3e3;
  }

  .participant-requirements {
    border-top-color: #e3e3e3;
  }
}
</style>
